import { useEffect, useRef, useState } from 'react'
import {
  X, MessageSquarePlus, Inbox, Loader2, CheckCircle2, ChevronDown, ChevronRight,
  ImagePlus,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { useSupportTickets, useMaakTicket, useTicketThumbnail } from './useSupportTickets'
import { maakThumbnail } from './utils/thumbnail'

const SCREENSHOT_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif']
const SCREENSHOT_MAX = 10 * 1024 * 1024  // 10MB

// Modulelijst voor de melding (aanpasbaar).
const MODULES = [
  'Offertes', 'Projecten', 'Klanten', 'Materialen', 'Kasten',
  'Productie', 'Facturen', 'Voorraad', 'Master DNA', 'Overig',
]
const TYPES = [
  { value: 'bug', label: 'Bug' },
  { value: 'klacht', label: 'Klacht' },
  { value: 'verzoek', label: 'Verzoek' },
  { value: 'vraag', label: 'Vraag' },
]

const STATUS_LABEL = {
  open: 'Open', in_behandeling: 'In behandeling', deels_opgelost: 'Deels opgelost',
  opgelost: 'Opgelost', backlog: 'Backlog',
}
const STATUS_BADGE = {
  open: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  in_behandeling: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
  deels_opgelost: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  opgelost: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  backlog: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
}
const PRIO_BADGE = {
  kritiek: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  hoog: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  gemiddeld: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  laag: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
}

const INPUT = 'w-full px-3 py-2 rounded-control border border-line dark:border-gray-600 bg-surface dark:bg-gray-700 text-ink dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-600/40'
const LABEL = 'block text-xs font-medium text-muted mb-1'

function Badge({ className, children }) {
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${className || ''}`}>{children}</span>
}

function fmtDatum(s) {
  if (!s) return ''
  try {
    return new Date(s).toLocaleDateString('nl-BE', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch {
    return s
  }
}

const LEEG_FORM = { type: 'bug', module: 'Offertes', titel: '', beschrijving: '', email: '' }

export default function SupportPanel({ isOpen, onClose }) {
  const [tab, setTab] = useState('indienen')
  const [form, setForm] = useState(LEEG_FORM)
  const [succes, setSucces] = useState(null)   // { ticket_nr, prioriteit }
  const [fout, setFout] = useState(null)
  const [openTicketId, setOpenTicketId] = useState(null)

  // Screenshot-state
  const [screenshotFile, setScreenshotFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [thumbBase64, setThumbBase64] = useState(null)
  const [thumbBezig, setThumbBezig] = useState(false)
  const [sleepActief, setSleepActief] = useState(false)
  const fileInputRef = useRef(null)

  const maak = useMaakTicket()
  const lijst = useSupportTickets()

  useEffect(() => {
    if (!isOpen) return undefined
    const handler = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isOpen, onClose])

  // Ctrl+V: plak een afbeelding uit het klembord (alleen op de indienen-tab).
  useEffect(() => {
    if (!isOpen || tab !== 'indienen') return undefined
    const onPaste = (e) => {
      const item = [...(e.clipboardData?.items || [])].find((i) => i.type.startsWith('image/'))
      if (item) {
        const f = item.getAsFile()
        if (f) kiesBestand(f)
      }
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tab])

  if (!isOpen) return null

  const kiesBestand = async (file) => {
    if (!file) return
    if (!SCREENSHOT_TYPES.includes(file.type)) {
      setFout('Niet-ondersteund afbeeldingstype (png/jpeg/webp/gif).')
      return
    }
    if (file.size > SCREENSHOT_MAX) {
      setFout('Screenshot is groter dan 10MB.')
      return
    }
    setFout(null)
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setScreenshotFile(file)
    setThumbBezig(true)
    const res = await maakThumbnail(file)
    setThumbBezig(false)
    if (res) {
      setThumbBase64(res.thumbnailBase64)
      setPreviewUrl(URL.createObjectURL(res.thumbnailBlob))
    } else {
      setThumbBase64(null)
      setPreviewUrl(URL.createObjectURL(file))
    }
  }

  const verwijderScreenshot = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setScreenshotFile(null)
    setPreviewUrl(null)
    setThumbBase64(null)
    setThumbBezig(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const submit = (e) => {
    e.preventDefault()
    setFout(null)
    if (!form.titel.trim() || !form.beschrijving.trim()) {
      setFout('Titel en beschrijving zijn verplicht.')
      return
    }
    const fd = new FormData()
    fd.append('titel', form.titel)
    fd.append('beschrijving', form.beschrijving)
    fd.append('type', form.type)
    fd.append('module', form.module)
    if (form.email.trim()) fd.append('email', form.email.trim())
    if (screenshotFile) fd.append('screenshot', screenshotFile)
    if (thumbBase64) fd.append('screenshot_thumbnail', thumbBase64)
    maak.mutate(fd, {
      onSuccess: (data) => {
        setSucces({ ticket_nr: data.ticket_nr, prioriteit: data.prioriteit })
        setForm(LEEG_FORM)
        verwijderScreenshot()
        toast.success(`Melding ${data.ticket_nr} ingediend`)
      },
      onError: (err) => setFout(err?.response?.data?.detail || 'Indienen mislukt. Probeer opnieuw.'),
    })
  }

  const setVeld = (veld) => (e) => setForm((f) => ({ ...f, [veld]: e.target.value }))

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-[420px] max-w-full bg-surface dark:bg-gray-800 shadow-xl border-l border-line dark:border-gray-700 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-line dark:border-gray-700">
          <h2 className="text-lg font-bold text-ink dark:text-white">Support</h2>
          <button onClick={onClose} className="p-1 rounded-control hover:bg-gray-100 dark:hover:bg-gray-700" aria-label="Sluiten">
            <X className="w-5 h-5 text-muted" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-line dark:border-gray-700">
          <TabKnop actief={tab === 'indienen'} onClick={() => setTab('indienen')} icon={MessageSquarePlus} label="Melding indienen" />
          <TabKnop actief={tab === 'meldingen'} onClick={() => setTab('meldingen')} icon={Inbox} label="Mijn meldingen" />
        </div>

        {/* Inhoud */}
        <div className="flex-1 overflow-y-auto p-5">
          {tab === 'indienen' ? (
            succes ? (
              <div className="text-center py-8">
                <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
                <p className="text-ink dark:text-white font-semibold mb-1">Melding ontvangen</p>
                <p className="text-sm text-muted mb-3">
                  Je ticket <span className="font-mono font-semibold">{succes.ticket_nr}</span> is geregistreerd. Je ontvangt een update zodra er nieuws is.
                </p>
                {succes.prioriteit && (
                  <p className="text-sm text-muted mb-5">
                    Ingeschatte prioriteit: <Badge className={PRIO_BADGE[succes.prioriteit]}>{succes.prioriteit}</Badge>
                  </p>
                )}
                <button
                  onClick={() => setSucces(null)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-control font-semibold"
                >
                  Nieuwe melding indienen
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={LABEL}>Type</label>
                    <select value={form.type} onChange={setVeld('type')} className={INPUT}>
                      {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={LABEL}>Module</label>
                    <select value={form.module} onChange={setVeld('module')} className={INPUT}>
                      {MODULES.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className={LABEL}>Titel</label>
                  <input type="text" value={form.titel} onChange={setVeld('titel')} maxLength={200} placeholder="Korte omschrijving" className={INPUT} />
                </div>
                <div>
                  <label className={LABEL}>Beschrijving</label>
                  <textarea value={form.beschrijving} onChange={setVeld('beschrijving')} rows={5} placeholder="Wat gebeurde er? Wat verwachtte je?" className={INPUT} />
                </div>
                <div>
                  <label className={LABEL}>E-mail (optioneel — voor updates)</label>
                  <input type="email" value={form.email} onChange={setVeld('email')} placeholder="jij@bedrijf.be" className={INPUT} />
                </div>

                <div>
                  <label className={LABEL}>Screenshot (optioneel)</label>
                  {previewUrl ? (
                    <div className="relative inline-block">
                      <img src={previewUrl} alt="Voorbeeld screenshot" className="max-h-40 rounded-control border border-line dark:border-gray-600" />
                      <button
                        type="button"
                        onClick={verwijderScreenshot}
                        className="absolute -top-2 -right-2 bg-gray-800 text-white rounded-full p-1 hover:bg-gray-700"
                        aria-label="Verwijder screenshot"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => { e.preventDefault(); setSleepActief(true) }}
                      onDragLeave={() => setSleepActief(false)}
                      onDrop={(e) => { e.preventDefault(); setSleepActief(false); kiesBestand(e.dataTransfer.files?.[0]) }}
                      className={`flex flex-col items-center justify-center gap-1 py-5 rounded-control border-2 border-dashed cursor-pointer text-center transition-colors ${
                        sleepActief
                          ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-line dark:border-gray-600 hover:border-blue-500'
                      }`}
                    >
                      {thumbBezig
                        ? <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                        : <ImagePlus className="w-5 h-5 text-muted" />}
                      <p className="text-xs text-muted">Sleep hierheen, klik, of plak (Ctrl+V)</p>
                      <p className="text-[10px] text-muted">PNG, JPG, WEBP, GIF — max 10MB</p>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    className="hidden"
                    onChange={(e) => kiesBestand(e.target.files?.[0])}
                  />
                </div>

                {fout && <p className="text-sm text-red-600">{fout}</p>}

                <button
                  type="submit"
                  disabled={maak.isPending}
                  className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-control font-semibold disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {maak.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                  {maak.isPending ? 'Wordt ingediend...' : 'Indienen'}
                </button>
              </form>
            )
          ) : (
            <MeldingenLijst lijst={lijst} openTicketId={openTicketId} setOpenTicketId={setOpenTicketId} />
          )}
        </div>
      </div>
    </div>
  )
}

function TabKnop({ actief, onClick, icon: Icon, label }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium border-b-2 transition-colors ${
        actief
          ? 'border-blue-600 text-blue-600'
          : 'border-transparent text-muted hover:text-ink dark:hover:text-white'
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  )
}

function MeldingenLijst({ lijst, openTicketId, setOpenTicketId }) {
  if (lijst.isLoading) {
    return <p className="text-sm text-muted py-8 text-center">Laden...</p>
  }
  if (lijst.isError) {
    return <p className="text-sm text-red-600 py-8 text-center">Kon meldingen niet laden.</p>
  }
  const tickets = lijst.data || []
  if (tickets.length === 0) {
    return <p className="text-sm text-muted py-8 text-center">Je hebt nog geen meldingen ingediend.</p>
  }
  return (
    <div className="space-y-2">
      {tickets.map((t) => {
        const open = openTicketId === t.id
        return (
          <div key={t.id} className="rounded-card border border-line dark:border-gray-700 overflow-hidden">
            <button
              onClick={() => setOpenTicketId(open ? null : t.id)}
              className="w-full text-left px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/50"
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="font-mono text-xs text-muted">{t.ticket_nr}</span>
                <span className="flex items-center gap-1.5">
                  <Badge className={PRIO_BADGE[t.prioriteit]}>{t.prioriteit}</Badge>
                  <Badge className={STATUS_BADGE[t.status]}>{STATUS_LABEL[t.status] || t.status}</Badge>
                  {open ? <ChevronDown className="w-4 h-4 text-muted" /> : <ChevronRight className="w-4 h-4 text-muted" />}
                </span>
              </div>
              <p className="text-sm font-medium text-ink dark:text-white truncate">{t.titel}</p>
              <div className="flex items-center justify-between gap-2 mt-0.5">
                <p className="text-xs text-muted">{t.module} · {fmtDatum(t.aangemaakt_op)}</p>
                {t.heeft_thumbnail && <TicketThumb ticketId={t.id} />}
              </div>
            </button>

            {open && (
              <div className="px-3 pb-3 pt-1 border-t border-line dark:border-gray-700 space-y-3">
                <div>
                  <p className="text-[10px] font-semibold text-muted uppercase tracking-wider mb-1">Jouw beschrijving</p>
                  <p className="text-sm text-ink dark:text-gray-200 whitespace-pre-wrap">{t.beschrijving}</p>
                </div>
                {(t.heeft_thumbnail || t.screenshot_url) && (
                  <div>
                    <p className="text-[10px] font-semibold text-muted uppercase tracking-wider mb-1">Screenshot</p>
                    {t.heeft_thumbnail && <TicketThumb ticketId={t.id} groot />}
                    {t.screenshot_url && (
                      <a
                        href={t.screenshot_url}
                        target="_blank"
                        rel="noreferrer"
                        className="block text-xs text-blue-600 hover:underline mt-1"
                      >
                        Bekijk origineel →
                      </a>
                    )}
                  </div>
                )}
                {t.ai_samenvatting && (
                  <div className="rounded-control bg-blue-50 dark:bg-blue-900/20 p-2.5">
                    <p className="text-[10px] font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wider mb-1">Samenvatting</p>
                    <p className="text-sm text-blue-900 dark:text-blue-200">{t.ai_samenvatting}</p>
                  </div>
                )}
                {(t.berichten || []).length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-muted uppercase tracking-wider mb-1.5">Berichten</p>
                    <div className="space-y-2">
                      {t.berichten.map((b) => (
                        <div
                          key={b.id}
                          className={`rounded-control p-2.5 text-sm ${
                            b.auteur === 'admin'
                              ? 'bg-blue-600 text-white ml-6'
                              : 'bg-gray-100 dark:bg-gray-700 text-ink dark:text-gray-200 mr-6'
                          }`}
                        >
                          <p className="whitespace-pre-wrap">{b.inhoud}</p>
                          <p className={`text-[10px] mt-1 ${b.auteur === 'admin' ? 'text-blue-100' : 'text-muted'}`}>
                            {b.auteur === 'admin' ? 'Support' : 'Jij'} · {fmtDatum(b.aangemaakt_op)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function TicketThumb({ ticketId, groot = false }) {
  const { data: url } = useTicketThumbnail(ticketId, true)
  if (!url) return null
  return (
    <img
      src={url}
      alt="Screenshot"
      className={groot
        ? 'max-h-40 rounded-control border border-line dark:border-gray-600'
        : 'w-10 h-10 rounded object-cover border border-line dark:border-gray-600'}
    />
  )
}
